/**
 * useSelectionCitations — bridges the writer's text selection to
 * Asthra citation lookups for the rail's citation popover. Spec
 * 26 C6.
 *
 * # DECISION (2026-05-08): chip-only fetch — no auto-debounced fetch.
 * # WHY: Writers reported the popover firing on every highlight and
 * #      drowning the rail in unwanted lookups. The "View citations
 * #      for selected text" chip is the explicit intent signal;
 * #      ``fetchNow`` is the only entry point.
 *
 * # DECISION (2026-05-11): drop the ``cursor`` param.
 * # WHY: useDocumentCursor is gone (it forced an ambient Word.run on
 * #      every selection event and hung Word on long CSR drafts).
 * #      ``fetchNow`` now reads the selection + section on demand at
 * #      click time via wordProbe.readSelectionText() + bookmark/
 * #      heading-walk fallback. Auto-clear of the popover on selection
 * #      shrink is also gone — clearing happens explicitly when the
 * #      popover is dismissed.
 */

import { useCallback } from "react";
import type { Citation, CitationDerivation, CitationResponse } from "../../api/client";
import { useCitations } from "../../hooks/useCitations";
import { readSelectionText } from "../../services/wordProbe";
import { detectCursorSectionNumberWithFallback } from "../../services/wordWriter";
import { extractContextForCitation } from "../services/citationContextWalker";

export interface UseSelectionCitationsParams {
  trial?: string;
  session?: string;
  docType: string;
  /** Minimum selection length to trigger a lookup. Default 5. */
  minLength?: number;
}

export interface UseSelectionCitationsResult {
  citations: Citation[];
  status: CitationResponse["status"] | null;
  message: string | null;
  transactionIdsUsed: string[];
  /** Computed numbers in the selection, with their trace and cited inputs. */
  derivations: CitationDerivation[];
  isLoading: boolean;
  error: string | null;
  /** The text the citations were fetched for — useful for the
   *  popover header ("Citations for selection · 'X…'"). */
  fetchedText: string;
  /** Force-fetch citations for the current cursor selection.
   *
   *  Only entry point — there is no auto-fetch on selection change.
   *  The "View citations for selected text" chip is the writer's
   *  explicit signal that they want a lookup.
   *
   *  Reads selection text + section number on demand via Word.run.
   *  No-ops silently when:
   *    - the selection is below ``minLength``,
   *    - or trial/session aren't set yet.
   *
   *  Returns a Promise that resolves once the underlying fetch
   *  completes, so callers can await if they want to chain UI
   *  effects (e.g. focus the popover after results arrive).
   */
  fetchNow: () => Promise<void>;
  /** False when this document has no (trial, session) identity — the
   *  citations endpoint cannot resolve anything, so ``fetchNow`` would be a
   *  silent no-op. Callers hide the "View citations" affordance then. */
  available: boolean;
  /** Force-fetch citations for a clicked citation marker. The walker
   *  assembles the prose between this marker and the previous one (or the
   *  section heading) and pipes it through the same fetch flow ``fetchNow``
   *  uses, so the popover renders identically regardless of trigger.
   *
   *  No-ops silently when the walker returns empty (deleted control, missing
   *  section bookmark, …) so a stray click never spawns a noisy popover. */
  fetchForCitationMarker: (params: {
    sectionNumber: string;
    controlId: number;
  }) => Promise<void>;
  /** Clear the popover. */
  clear: () => void;
}

const DEFAULT_MIN_LENGTH = 5;

/** Pure precondition guard for ``fetchNow``. A fetch can only succeed
 *  when:
 *
 *    - the trimmed selection is at least ``minLength`` characters
 *      (single-word drags are rarely useful provenance lookups), AND
 *    - both ``trial`` and ``session`` are non-empty (the server maps
 *      ``(section, docType, trial, session)`` to the module file on
 *      disk; without trial+session the lookup silently returns empty).
 *
 *  Exported so tests pin the rule once.
 */
export function canFetchSelection(
  text: string,
  trial: string | undefined,
  session: string | undefined,
  minLength: number,
): boolean {
  return text.trim().length >= minLength && !!trial && !!session;
}

export function useSelectionCitations(params: UseSelectionCitationsParams): UseSelectionCitationsResult {
  const { trial, session, docType, minLength = DEFAULT_MIN_LENGTH } = params;

  const {
    citations,
    status,
    message,
    transactionIdsUsed,
    derivations,
    selectedText,
    isLoading,
    error,
    fetchCitations,
    clear,
  } = useCitations({ trial, session, docType });

  /** Common tail: precondition-guard the assembled text + section and
   *  dispatch to the citation-fetch hook. Shared by ``fetchNow`` (live
   *  Word selection) and ``fetchForCitationMarker`` (cursor entered a
   *  citation content control) so both paths render identically. */
  const fetchForText = useCallback(
    async (text: string, section: string) => {
      const trimmed = text.trim();
      if (!canFetchSelection(trimmed, trial, session, minLength)) return;
      await fetchCitations(trimmed, section);
    },
    [trial, session, minLength, fetchCitations],
  );

  const fetchNow = useCallback(async () => {
    const { text } = await readSelectionText();
    // Same bookmark-probe → heading-walk chain the scope chip uses, so the
    // popover can never disagree with the chip: docs loaded from a
    // server-built docx (no ASTHRA_SEC_* bookmarks) still resolve via the
    // heading walk instead of sending section="" to the server.
    const section = (await detectCursorSectionNumberWithFallback()) ?? "";
    await fetchForText(text, section);
  }, [fetchForText]);

  const fetchForCitationMarker = useCallback(
    async ({ sectionNumber, controlId }: { sectionNumber: string; controlId: number }) => {
      const result = await extractContextForCitation({ sectionNumber, controlId });
      if (result.empty) return;
      // The control's tag already carries the section number — we trust it
      // over a re-walked bookmark resolution because (a) it's cheaper and
      // (b) it survives the rare case where the writer has the cursor at
      // a section boundary and the bookmark walker disagrees with the
      // control's stored section.
      await fetchForText(result.text, sectionNumber);
    },
    [fetchForText],
  );

  return {
    citations,
    status,
    message,
    transactionIdsUsed,
    derivations,
    isLoading,
    error,
    fetchedText: selectedText,
    fetchNow,
    available: Boolean(trial && session),
    fetchForCitationMarker,
    clear,
  };
}
